import {Spec} from 'immutability-helper';
import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import {
  buildTableSearchResult,
  doesTableCellMatch,
  getSearchResultsForColumnNames,
  getSearchResultsForTableName,
} from 'notebook/mediocre/search/TableSearchUtil';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import RegistrationTableConfigComponent from 'notebook/registration-tables/RegistrationTableConfig';
import {
  RegistrationTableConfig,
  RegistrationTableType,
} from 'notebook/registration-tables/RegistrationTableConstants';
import {
  convertRegistrationTableConfigToTable,
  mergeTableUpdateIntoRegistrationTableConfig,
} from 'notebook/registration-tables/RegistrationTableNotePartUtil';
import * as RegistrationTableUtil from 'notebook/registration-tables/RegistrationTableUtil';
import TableWithMissingSchema from 'notebook/structured-tables/components/TableWithMissingSchema';
import {BasicTableProps} from 'notebook/structured-tables/StructuredTableConstants';
import {CollapsedTableState} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {Table, TableData} from 'notebook/tables/constants/TableConstants';
import {OutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {isTableCollapsed} from 'notebook/tables/store/logic/TableVisibilityLogic';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import APIUtil from 'util/APIUtil';
import {MakeFieldsRequired} from 'util/Types';

function doesUserHavePermissionToViewTable(
  registrationTable: RegistrationTableConfig
): registrationTable is RegistrationTableConfig & {tagSchema: TagSchema} {
  return (
    registrationTable.tagSchema != null &&
    (registrationTable.tableType !== RegistrationTableType.BATCH || registrationTable.batchSchema != null)
  );
}

const REGISTRATION_TABLE_CONFIG_PLUGIN: NoteEditorPlugin = {
  type: ModelTypeConstants.REGISTRATION_TABLE_CONFIG,
  typeClass: 'is-table',
  title: 'Registration Table',
  icon: 'note-table',
  component: {
    selectionInit: MediocrePlugins.table.selectionInit,
    selectionAtStart: MediocrePlugins.table.selectionAtStart,
    selectionAtEnd: MediocrePlugins.table.selectionAtEnd,
    required: [],
    getItemProps(item: NotePart & {item: RegistrationTableConfig}, index: number): TableItemProps {
      return {
        ...MediocrePlugins.getTableItemProps.call(this, item, index),
      };
    },
    getIsCollapsed(
      item: NotePart & {item: RegistrationTableConfig},
      editorProps: NoteEditorProps,
      collapsedStateById: CollapsedTableState
    ) {
      const rowCount = item.item.rows.length;
      const cellCount = rowCount * item.item.columns.length;
      return isTableCollapsed({
        collapseId: item.cid,
        collapsedStateById,
        rowCount,
        cellCount,
        tableKind: null,
      });
    },
    getIsInaccessible(item: NotePart & {item: RegistrationTableConfig}): boolean {
      return !doesUserHavePermissionToViewTable(item.item);
    },
    // Plugin methods are always called using Function.prototype.call,
    // so `this` refers to the editor component.
    make(
      this: NoteEditor,
      item: NotePart & {item: RegistrationTableConfig},
      index: number,
      editorProps: NoteEditorProps,
      itemProps: TableItemProps,
      searchResultProps: MediocreSearchResultProps
    ): JSX.Element {
      const cid = editorProps.getCid(item);
      const registrationTableConfig: RegistrationTableConfig = item.item;
      const {name, provenanceUuid, rows, columns, labelingStrategy, isWatchingInputTable, tableType} =
        registrationTableConfig;
      const sharedProps: MakeFieldsRequired<BasicTableProps, 'tableTypeLabel'> = {
        icon: 'register',
        indentation: this.getIndentation(item),
        tableTypeLabel: 'Registration',
        tableName: name,
      };
      if (!doesUserHavePermissionToViewTable(registrationTableConfig)) {
        return <TableWithMissingSchema {...sharedProps} />;
      }
      return (
        <RegistrationTableConfigComponent
          {...sharedProps}
          {...itemProps}
          rows={rows}
          structuredTableColumns={columns}
          labelingStrategy={labelingStrategy}
          provenanceUuid={provenanceUuid}
          tagSchema={registrationTableConfig.tagSchema}
          batchSchema={registrationTableConfig.batchSchema}
          isWatchingInputTable={isWatchingInputTable}
          tableType={tableType}
          editor={this}
          editorProps={editorProps}
          // Standard table props
          cid={cid}
          index={index}
          mouseDownHandler={this.itemMouseDownHandler}
          mouseOverHandler={this.itemMouseOverHandler}
          updateHandler={this.itemUpdateHandler}
          {...searchResultProps}
        />
      );
    },
  },
  itemToHTML(this: NoteEditor, $el: JQuery, item: NotePart & {item: RegistrationTableConfig}): JQuery {
    // TODO(alan)
    $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID, this.props.fileId);
    return $el;
  },
  // Plugin methods are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemFromHTML(
    this: NoteEditor,
    $el: JQuery,
    item: NotePart & {item: RegistrationTableConfig}
  ): NotePart | null {
    const fileType = APIUtil.apiIdToType(this.props.fileId);
    const sourceFileType = APIUtil.apiIdToType(
      $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID)
    );
    if ([ModelTypeConstants.ENTRY, ModelTypeConstants.STAGE_ENTRY].includes(fileType)) {
      notifications.warn('Cannot paste registration tables from template to entry.');
      return null;
    } else if (fileType !== sourceFileType) {
      notifications.warn('Cannot paste registration table.');
      return null;
    } else if (
      RegistrationTableUtil.getRegistrationTableConfigsInNote(this.props.note)
        .map((registrationTableConfig) => registrationTableConfig.provenanceUuid)
        .includes(item.item.provenanceUuid)
    ) {
      notifications.warn('Cannot create more than one copy of the same registration table.');
      return null;
    } else {
      return item;
    }
  },
  // Agent keep in sync: table.name returned from getContent
  getSpreadsheetTableName({item}: NotePart & {item: RegistrationTableConfig}): string {
    return item.name;
  },
  // Plugin methods are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  getContent(this: NoteEditor, {item}: NotePart & {item: RegistrationTableConfig}): {table: Table} {
    return {table: convertRegistrationTableConfigToTable(item, this.props.fileId)};
  },
  setContent({
    item,
    table,
    isWatchingInputTable,
  }: {
    item?: RegistrationTableConfig;
    table?: TableData;
    isWatchingInputTable: boolean;
  }): Spec<NotePart> {
    if (isWatchingInputTable != null) {
      return {item: {$merge: {isWatchingInputTable}}};
    } else if (item) {
      return {$merge: {item}};
    } else if (table) {
      return {
        item: {
          $apply(registrationTableConfig: RegistrationTableConfig): RegistrationTableConfig {
            return mergeTableUpdateIntoRegistrationTableConfig(registrationTableConfig, table);
          },
        },
      };
    }

    assert(false, 'setContent invoked without content to set.');
  },
  getStoreDependenciesForSpreadsheetEval(
    item: NotePart & {item: RegistrationTableConfig}
  ): StoreDependencyMap {
    return {};
  },
  *getSearchResults(
    item: NotePart,
    matcher: TextMatcher,
    searchOverFormulas: boolean,
    computedTables: OutputTableMap | null,
    isCollapsed: boolean
  ): Generator<MediocreSearchResult> {
    const registrationTableConfig = item.item as RegistrationTableConfig;
    // Search over the table name
    yield* getSearchResultsForTableName(matcher, registrationTableConfig.name, item.cid);

    if (!isCollapsed) {
      // Search over the table column names
      const columnNames = registrationTableConfig.columns.map((column) => column.name ?? '');
      yield* getSearchResultsForColumnNames(matcher, columnNames, item.cid);

      const computedTable = computedTables?.[registrationTableConfig.name];
      assert(computedTable != null, 'Computed table not found in plugin', {
        computedTableNames: computedTables ? Object.keys(computedTables) : [],
      });

      for (const [i, row] of registrationTableConfig.rows.entries()) {
        for (const [j, cell] of row.cells.entries()) {
          if (doesTableCellMatch(matcher, cell, computedTable[i][j], searchOverFormulas)) {
            yield buildTableSearchResult(item.cid, i, j);
          }
        }
      }
    }
  },
};

/*
 * This plugin is no longer supported, please do not further proliferate its use.
 */
export default REGISTRATION_TABLE_CONFIG_PLUGIN;
